/* Collection filters are server-rendered. Full-collection search loads only on demand. */
if (!customElements.get('category-page')) {
  customElements.define('category-page', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-category-grid]');
      this.pagination = this.querySelector('[data-category-pagination]');
      this.count = this.querySelector('[data-category-count]');
      this.status = this.querySelector('[data-category-status]');
      this.searchForm = this.querySelector('[data-category-search]');
      this.searchInput = this.searchForm.querySelector('[name="q"]');
      this.original = {
        cards: [...this.grid.childNodes].map(node => node.cloneNode(true)),
        pagination: [...this.pagination.childNodes].map(node => node.cloneNode(true)),
        count: this.count.textContent
      };
      this.pageSize = Number(this.dataset.pageSize) || 6;
      this.sequence = 0;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.searchForm.addEventListener('submit', event => {
        event.preventDefault();
        this.search();
      }, options);
      this.searchInput.addEventListener('input', () => {
        if (!this.searchInput.value.trim()) this.search();
      }, options);
      this.querySelectorAll('[data-auto-submit]').forEach(select => {
        select.addEventListener('change', () => {
          this.querySelector('[data-preserve-query]').value = this.searchInput.value.trim();
          select.form.requestSubmit();
        }, options);
      });
      this.querySelectorAll('[data-line-link]').forEach(link => {
        link.addEventListener('click', () => {
          const url = new URL(link.href);
          const query = this.searchInput.value.trim();
          if (query) url.searchParams.set('category_q', query);
          else url.searchParams.delete('category_q');
          link.href = url.href;
        }, options);
      });
      const initialQuery = new URL(location.href).searchParams.get('category_q');
      if (initialQuery) {
        this.searchInput.value = initialQuery;
        this.search();
      }
    }

    disconnectedCallback() {
      this.sequence++;
      this.controller?.abort();
      this.listeners?.abort();
      if (this.original) this.restore();
      this.initialized = false;
    }

    restore() {
      this.grid.replaceChildren(...this.original.cards.map(node => node.cloneNode(true)));
      this.pagination.replaceChildren(...this.original.pagination.map(node => node.cloneNode(true)));
      this.count.textContent = this.original.count;
      this.status.textContent = '';
      this.grid.removeAttribute('aria-busy');
    }

    updateURL(query) {
      const url = new URL(location.href);
      if (query) url.searchParams.set('category_q', query);
      else url.searchParams.delete('category_q');
      history.replaceState(history.state, '', url);
      this.querySelector('[data-preserve-query]').value = query;
    }

    async loadIndex(signal) {
      if (this.index) return this.index;
      const base = new URL(this.dataset.collectionUrl, location.origin);
      let url = new URL(base);
      url.search = location.search;
      url.searchParams.delete('page');
      url.searchParams.delete('category_q');
      url.searchParams.set('view', 'category-search');
      const visited = new Set();
      const cards = [];
      let expectedTotal;
      while (url) {
        if (url.origin !== base.origin || url.pathname !== base.pathname || visited.has(url.href)) {
          throw new Error('Invalid collection index pagination');
        }
        visited.add(url.href);
        const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Collection index request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const index = document.querySelector('[data-category-index]');
        if (!index) throw new Error('Collection search view is missing');
        if (expectedTotal === undefined) expectedTotal = Number(index.dataset.total);
        cards.push(...index.querySelectorAll('[data-category-product]'));
        if (index.dataset.nextUrl) {
          url = new URL(index.dataset.nextUrl, url);
          url.searchParams.set('view', 'category-search');
        } else url = null;
      }
      // Never report partial results as complete (including Shopify pagination limits).
      if (!Number.isFinite(expectedTotal) || cards.length !== expectedTotal) throw new Error('Incomplete collection index');
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      this.index = cards;
      return cards;
    }

    async search() {
      const sequence = ++this.sequence;
      this.controller?.abort();
      const query = this.searchInput.value.trim();
      this.updateURL(query);
      if (!query) {
        this.restore();
        return;
      }
      this.controller = new AbortController();
      this.status.textContent = this.dataset.loadingLabel;
      this.grid.setAttribute('aria-busy', 'true');
      try {
        const cards = await this.loadIndex(this.controller.signal);
        if (sequence !== this.sequence) return;
        const words = query.normalize('NFKC').toLocaleLowerCase().split(/\s+/u).filter(Boolean);
        this.matches = cards.filter(card => {
          const text = card.dataset.search.normalize('NFKC').toLocaleLowerCase();
          return words.every(word => text.includes(word));
        });
        this.renderSearchPage(1);
      } catch (error) {
        if (sequence !== this.sequence || error.name === 'AbortError') return;
        this.status.textContent = this.dataset.errorLabel;
      } finally {
        if (sequence === this.sequence) this.grid.removeAttribute('aria-busy');
      }
    }

    renderSearchPage(page) {
      const start = (page - 1) * this.pageSize;
      const cards = this.matches.slice(start, start + this.pageSize).map(source => {
        const card = source.cloneNode(true);
        const parts = [];
        if (this.dataset.showSku === 'true' && card.dataset.model) parts.push(card.dataset.model);
        if (this.dataset.detailsLabel) parts.push(this.dataset.detailsLabel);
        card.querySelector('.catalog-product__details').textContent = parts.join(' · ');
        const missing = card.querySelector('.catalog-product__missing');
        if (missing) missing.textContent = this.dataset.missingImage;
        return card;
      });
      if (!cards.length) {
        const empty = document.createElement('p');
        empty.className = 'category-page__empty';
        empty.textContent = this.dataset.emptyLabel;
        cards.push(empty);
      }
      this.grid.replaceChildren(...cards);
      const count = `${this.matches.length} ${this.dataset.countLabel}`;
      this.count.textContent = count;
      this.status.textContent = count;
      this.pagination.replaceChildren();
      const pages = Math.ceil(this.matches.length / this.pageSize);
      if (pages <= 1) return;
      const addButton = (label, target, disabled) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.disabled = disabled;
        button.addEventListener('click', () => {
          this.renderSearchPage(target);
          this.grid.setAttribute('tabindex', '-1');
          this.grid.focus({ preventScroll: true });
          this.grid.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
        this.pagination.append(button);
      };
      addButton(this.dataset.previousLabel, page - 1, page === 1);
      const current = document.createElement('span');
      current.textContent = `${page} / ${pages}`;
      current.setAttribute('aria-current', 'page');
      this.pagination.append(current);
      addButton(this.dataset.nextLabel, page + 1, page === pages);
    }
  });
}
